import * as moment from 'moment';
import * as React from 'react';
import { Alert, Button, FormControl, Glyphicon, Panel, Table } from 'react-bootstrap';
import { IPaystub } from '@nkr/models/index';
import { formatMoneyInput } from '../../common/money-input';
import { IPaystubOverview } from '../utils/verify-stub.utils';
import VerifyIncomeActions from './VerifyIncomeActions';

const paystubDateColumnStyle: React.CSSProperties = {
    width: '24%',
    minWidth: 0,
    whiteSpace: 'nowrap'
};

const paystubGrossColumnStyle: React.CSSProperties = {
    width: '160px',
    minWidth: '160px',
    textAlign: 'right',
    verticalAlign: 'middle'
};

const paystubGrossInputStyle: React.CSSProperties = {
    textAlign: 'right',
    fontSize: '14px',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box'
};

const paystubDateInputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '6px 8px',
    fontSize: '14px',
    lineHeight: '1.4'
};

const paystubCheckboxStyle = (checked: boolean) => ({
    cursor: 'pointer' as const,
    width: '26px',
    height: '26px',
    border: '2px solid #ffc107',
    borderRadius: '5px',
    backgroundColor: checked ? '#ffc107' : 'transparent',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    position: 'relative' as const,
    ...(checked && {
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M10 3L4.5 8.5L2 6' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '14px 14px'
    })
});

const getPaystubRowStyle = (isViewing: boolean, reviewRequired: boolean, isApproved: boolean): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
        borderBottom: '1px solid #f0f0f0',
        borderLeft: '4px solid transparent',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, border-left-color 0.2s ease, box-shadow 0.2s ease'
    };

    if (reviewRequired) {
        baseStyle.borderLeft = isViewing ? '4px solid #0d6efd' : '4px solid #ffc107';
        baseStyle.backgroundColor = '#fff3cd';

        if (isViewing) {
            baseStyle.boxShadow = 'inset 4px 0 0 #0d6efd, 0 1px 4px rgba(13, 110, 253, 0.2)';
        }

        return baseStyle;
    }

    if (isApproved) {
        baseStyle.borderLeft = isViewing ? '4px solid #0d6efd' : '4px solid #28a745';
        baseStyle.backgroundColor = '#d4edda';

        if (isViewing) {
            baseStyle.boxShadow = 'inset 4px 0 0 #0d6efd, 0 1px 4px rgba(40, 167, 69, 0.2)';
        }

        return baseStyle;
    }

    if (isViewing) {
        baseStyle.borderLeft = '4px solid #0d6efd';
        baseStyle.backgroundColor = '#d1ecf1';
        baseStyle.boxShadow = 'inset 4px 0 0 #0d6efd, 0 1px 4px rgba(13, 110, 253, 0.2)';
    }

    return baseStyle;
};

interface IProps {
    overview: IPaystubOverview;
    isHourly: boolean;
    weeklyPayByPayStub: number;
    paystubs: IPaystub[];
    hasHeaderPayStubSummary: boolean;
    editingIndex: number | null;
    grossFocusIndex: number | null;
    approvedPaystubIds: Set<number>;
    isPaystubViewing: (paystub: IPaystub) => boolean;
    onRowClick: (event: React.MouseEvent, paystub: IPaystub) => void;
    onReviewRequiredChange: (paystub: IPaystub, checked: boolean) => void;
    onPaystubChange: (index: number, field: 'startDate' | 'endDate' | 'grossAmount', value: string | number) => void;
    onModifyClick: (index: number, isEditing: boolean) => void;
    onGrossFocus: (index: number | null) => void;
    onDecline: () => void;
    onVerify: () => void;
}

const WagePaystubIncomeSection = (props: IProps) => {
    const reviewLabel = props.overview.stubCount === 0
        ? '—'
        : String(props.overview.flaggedCount);
    const reviewDetail = props.overview.stubCount === 0
        ? 'Waiting on paystub lines'
        : (props.overview.flaggedCount > 0
            ? `${props.overview.flaggedCount} of ${props.overview.stubCount} need review`
            : 'None marked yet');

    const showWeeklyHero = props.isHourly && props.overview.stubCount > 1 && props.weeklyPayByPayStub > 0;
    const heroAmount = showWeeklyHero ? props.weeklyPayByPayStub : props.overview.totalGross;
    const heroMissing = showWeeklyHero ? !(props.weeklyPayByPayStub > 0) : props.overview.missingGross;

    return (
        <>
            <Panel>
                <Panel.Heading>
                    <Panel.Title componentClass="h3">Overview</Panel.Title>
                </Panel.Heading>
                <Panel.Body style={{ padding: '15px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{
                            flex: '1 1 180px',
                            minWidth: 160,
                            padding: '14px 16px',
                            borderRadius: 8,
                            backgroundColor: props.overview.missingDates ? '#fff3cd' : '#f8fafc',
                            border: props.overview.missingDates ? '1px solid #ffc107' : '1px solid #e5e7eb'
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 6 }}>
                                Coverage
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 650, color: props.overview.missingDates ? '#856404' : '#111827', lineHeight: 1.25 }}>
                                {props.overview.coverage}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                {props.overview.stubCount > 0
                                    ? `${props.overview.stubCount} paystub${props.overview.stubCount === 1 ? '' : 's'}`
                                    : 'No paystub lines'}
                            </div>
                        </div>
                        <div style={{
                            flex: '1 1 180px',
                            minWidth: 160,
                            padding: '14px 16px',
                            borderRadius: 8,
                            backgroundColor: heroMissing ? '#fff3cd' : '#f8fafc',
                            border: heroMissing ? '1px solid #ffc107' : '1px solid #e5e7eb'
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 6 }}>
                                {showWeeklyHero ? 'Paystub weekly' : 'Combined gross'}
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 650, color: heroMissing ? '#856404' : '#111827', lineHeight: 1.25 }}>
                                ${formatMoneyInput(heroAmount)}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                {heroMissing
                                    ? (showWeeklyHero ? 'Weekly is missing or $0' : 'Gross is missing or $0')
                                    : (showWeeklyHero
                                        ? `Combined $${formatMoneyInput(props.overview.totalGross)} across ${props.overview.stubCount} stubs`
                                        : 'Sum of stub wages')}
                            </div>
                        </div>
                        <div style={{
                            flex: '1 1 180px',
                            minWidth: 160,
                            padding: '14px 16px',
                            borderRadius: 8,
                            backgroundColor: props.overview.flaggedCount > 0 ? '#fff3cd' : '#f8fafc',
                            border: props.overview.flaggedCount > 0 ? '1px solid #ffc107' : '1px solid #e5e7eb'
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 6 }}>
                                Flagged for review
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 650, color: props.overview.flaggedCount > 0 ? '#856404' : '#111827', lineHeight: 1.25 }}>
                                {reviewLabel}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                {reviewDetail}
                            </div>
                        </div>
                    </div>
                </Panel.Body>
            </Panel>

            <Panel style={{ marginTop: 15 }}>
                <Panel.Heading>
                    <Panel.Title componentClass="h3">Verification Required</Panel.Title>
                </Panel.Heading>
                <Panel.Body style={{ padding: '15px' }}>
                    {props.paystubs.length === 0 && props.hasHeaderPayStubSummary && (
                        <Alert bsStyle='warning' style={{ marginBottom: 15 }}>
                            No pay stub line items were returned for this request, but summary fields are present on the
                            request record. Overview below may reflect header-only data until wage reimbursement data is
                            consistent.
                        </Alert>
                    )}
                    {props.paystubs.length > 0 && (
                        <div>
                            <h4 style={{ marginTop: 0, marginBottom: 10, fontSize: '15px', fontWeight: 600, color: '#333' }}>Paystubs</h4>
                            <Table bordered style={{ marginBottom: 0, tableLayout: 'fixed', width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '52px', textAlign: 'center' }} title='Mark the paystub if dates or amounts need finance review'>
                                            Flag
                                        </th>
                                        <th style={{ width: '40px', textAlign: 'center' }}>#</th>
                                        <th style={{ width: '24%' }}>Start Date</th>
                                        <th style={{ width: '24%' }}>End Date</th>
                                        <th style={paystubGrossColumnStyle}>Gross</th>
                                        <th style={{ width: '56px', textAlign: 'center' }}>Edit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {props.paystubs.map((paystub: IPaystub, index: number) => {
                                        const isEditing = props.editingIndex === index;

                                        return (
                                            <tr
                                                key={paystub.id || index}
                                                onClick={(event) => props.onRowClick(event, paystub)}
                                                style={getPaystubRowStyle(
                                                    props.isPaystubViewing(paystub),
                                                    paystub.reviewRequired === true,
                                                    paystub.id != null && props.approvedPaystubIds.has(paystub.id)
                                                )}>
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle', width: '52px' }}>
                                                    <input
                                                        type='checkbox'
                                                        title='Flag this paystub for finance review'
                                                        checked={paystub.reviewRequired === true}
                                                        disabled={!paystub.id}
                                                        onChange={(e) => props.onReviewRequiredChange(paystub, e.currentTarget.checked)}
                                                        style={paystubCheckboxStyle(paystub.reviewRequired === true)} />
                                                </td>
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle', width: '40px' }}>{paystub.paystubSequence || index + 1}</td>
                                                <td style={paystubDateColumnStyle}>
                                                    {isEditing ? (
                                                        <FormControl
                                                            type='date'
                                                            value={moment(paystub.startDate).format('YYYY-MM-DD')}
                                                            onChange={(e: React.FormEvent<HTMLInputElement & FormControl>) => props.onPaystubChange(index, 'startDate', e.currentTarget.value)}
                                                            style={paystubDateInputStyle} />
                                                    ) : (
                                                        <span>{moment(paystub.startDate).format('MM/DD/YYYY')}</span>
                                                    )}
                                                </td>
                                                <td style={paystubDateColumnStyle}>
                                                    {isEditing ? (
                                                        <FormControl
                                                            type='date'
                                                            value={moment(paystub.endDate).format('YYYY-MM-DD')}
                                                            onChange={(e: React.FormEvent<HTMLInputElement & FormControl>) => props.onPaystubChange(index, 'endDate', e.currentTarget.value)}
                                                            style={paystubDateInputStyle} />
                                                    ) : (
                                                        <span>{moment(paystub.endDate).format('MM/DD/YYYY')}</span>
                                                    )}
                                                </td>
                                                <td style={paystubGrossColumnStyle}>
                                                    {isEditing ? (
                                                        <div className='input-group' style={{ width: '100%' }}>
                                                            <span className='input-group-addon'>$</span>
                                                            <FormControl
                                                                type='text'
                                                                inputMode='decimal'
                                                                value={props.grossFocusIndex === index
                                                                    ? (paystub.grossAmount ? String(paystub.grossAmount) : '')
                                                                    : formatMoneyInput(paystub.grossAmount || 0)}
                                                                onFocus={() => props.onGrossFocus(index)}
                                                                onBlur={() => props.onGrossFocus(null)}
                                                                onChange={(e: React.FormEvent<HTMLInputElement & FormControl>) => props.onPaystubChange(index, 'grossAmount', e.currentTarget.value)}
                                                                style={paystubGrossInputStyle} />
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                                                            ${formatMoneyInput(paystub.grossAmount || 0)}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle', width: '56px' }}>
                                                    <Button
                                                        title={isEditing ? 'Done' : 'Edit'}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            props.onModifyClick(index, isEditing);
                                                        }}
                                                        style={{
                                                            minWidth: '36px',
                                                            minHeight: '32px',
                                                            padding: '4px 8px'
                                                        }}>
                                                        <Glyphicon
                                                            glyph={isEditing ? 'ok' : 'pencil'}
                                                            style={{ fontSize: '16px' }} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {!props.isHourly && props.paystubs.length === 0 && (
                        <div style={{ padding: '15px 0', color: '#666', textAlign: 'center' }}>
                            No paystubs to verify
                        </div>
                    )}
                </Panel.Body>
            </Panel>

            <VerifyIncomeActions
                onDecline={props.onDecline}
                onVerify={props.onVerify}
                declineDisabled={props.overview.flaggedCount === 0}
                verifyDisabled={props.overview.flaggedCount > 0}
                declineTitle={props.overview.flaggedCount === 0 ? 'Flag at least one paystub for review before declining' : 'Decline'}
                verifyTitle={props.overview.flaggedCount > 0 ? 'Remove paystub review flags before verifying' : undefined}
            />
        </>
    );
};

export default WagePaystubIncomeSection;
